import { createFileRoute, redirect } from '@tanstack/react-router';
import posthog from 'posthog-js';
import { z } from 'zod';

import { isPersonExecutive } from 'helpers/person';
import { zFiltersArray } from 'helpers/router';
import { resolveSession } from 'helpers/session';
import { NotFound } from 'pages/NotFound';

// NOTE: This schema is a relic of the TanStack Router migration — every route
// under `/_private` inherits these params, which caused route-level params of
// the same name to be clobbered on merge. Params are being migrated to the
// specific routes that use them. The remaining entries are blocked on shared
// seams (Table pagination/sorting hooks, the useDialog/Forms/Modals shells, and
// the PeopleTable filter cluster) and are removed in later phases.
const privateSearchSchema = z.object({
  action: z.string().optional().catch(undefined),
  advisor_id: zFiltersArray(),
  dialog: z.string().optional().catch(undefined),
  form: z.string().optional().catch(undefined),
  function_expertise: zFiltersArray(),
  industry_expertise: zFiltersArray(),
  interest_role_board_advisory: zFiltersArray(),
  interest_role_operating: zFiltersArray(),
  modal: z.string().optional().catch(undefined),
  operating_highlights: zFiltersArray(),
  order: z
    .union([z.literal('asc'), z.literal('desc')])
    .optional()
    .catch(undefined),
  page: z.coerce.number().optional().catch(0),
  results: z.coerce.number().optional().catch(0),
  sort: z.string().optional().catch(undefined),
  status: zFiltersArray(),
  tier: zFiltersArray(),
});

export const Route = createFileRoute('/_private')({
  notFoundComponent: NotFound,
  beforeLoad: async ({ context, location, params }) => {
    // Resolve the user and role here, together, instead of reading them from
    // `context`: the router spreads this route's previous return value over
    // the parent context, so `context.user` and `context.role` would be the
    // values from the last load (BAN-6234, and a signed-out user sent to
    // the no-team page instead of /login).
    const teamId = 'teamId' in params ? params.teamId : undefined;
    const { role, user } = await resolveSession(
      context.queryClient,
      typeof teamId === 'string' ? teamId : undefined,
    );

    if (!user) {
      const searchPart =
        typeof location.searchStr === 'string' ? location.searchStr : '';
      const hashPart = typeof location.hash === 'string' ? location.hash : '';
      const continuePath = location.pathname + searchPart + hashPart;
      const hasMeaningfulContinue =
        continuePath.startsWith('/') && continuePath.length > 1;
      throw redirect({
        ...(hasMeaningfulContinue && {
          search: { continue: continuePath },
        }),
        to: '/login',
      });
    }

    // Do not gate the signed-in shell on PostHog: `isFeatureEnabled` is false until
    // flags load, which caused /login ↔ / redirect loops for executive users.
    // Use `is_executive` from the API; use flag `project-exec-tech` inside the app only.
    const hasPrivateAccess = Boolean(role) || isPersonExecutive(user);

    const isProfilePath =
      location.pathname === '/profile' ||
      location.pathname.startsWith('/profile/');

    if (!hasPrivateAccess && !isProfilePath) {
      // Signed in but on no team and not an executive: only their profile is
      // open to them, so show their (empty) team list. Not /login: the guest
      // layout bounces signed-in users to / and causes a redirect loop.
      throw redirect({
        replace: true,
        to: '/profile/teams',
      });
    }

    if (posthog.isFeatureEnabled('maintenance')) {
      throw redirect({
        to: '/maintenance',
      });
    }

    return { role, user };
  },
  validateSearch: privateSearchSchema,
});
